import { For, Show } from "solid-js";
import {
  PACKS,
  abilityDef,
  buildCost,
  buildSize,
  pieceDef,
  type BoardConfig,
  type CardTypeId,
  type DeckBuild,
  type PackDef,
  type PieceTypeId,
  type PlayerId,
} from "../../core";
import { PLAYER_ACCENT } from "../../app/config";
import { STARTER_DECKS } from "../../app/decks";
import { PieceIcon } from "./PieceIcon";
import { MovePattern } from "./MovePattern";

export interface DeckBuilderProps {
  owner: PlayerId;
  name: string;
  build: DeckBuild;
  config: BoardConfig;
  onName: (name: string) => void;
  onChange: (type: CardTypeId, count: number) => void;
}

/** A normalised deck-builder row, whether it's a piece or an ability card. */
interface Entry {
  readonly id: CardTypeId;
  readonly name: string;
  readonly cost: number;
  readonly desc: string;
  /** Max copies allowed per deck, or undefined for no limit. */
  readonly max?: number;
  /** Set for pieces (rendered with the piece art); abilities use `glyph`. */
  readonly pieceType?: PieceTypeId;
  readonly glyph?: string;
}

function packEntries(pack: PackDef): Entry[] {
  const pieces: Entry[] = pack.pieces.map((id) => {
    const def = pieceDef(id);
    return {
      id: def.id,
      name: def.name,
      cost: def.cost,
      desc: def.description ?? `moves like a ${def.name.toLowerCase()}`,
      max: def.maxCopies,
      pieceType: def.id,
    };
  });
  const abilities: Entry[] = pack.abilities.map((id) => {
    const def = abilityDef(id);
    return { id: def.id, name: def.name, cost: def.cost, desc: def.blurb, glyph: def.glyph };
  });
  return [...pieces, ...abilities];
}

export function DeckBuilder(props: DeckBuilderProps) {
  const spent = () => buildCost(props.build);
  const remaining = () => props.config.budget - spent();
  const over = () => spent() > props.config.budget;
  const pct = () => Math.min(100, (spent() / props.config.budget) * 100);

  function applyPreset(build: DeckBuild) {
    // Iterate exactly the cards the builder displays (every pack), so two-cell
    // terrain and the Captain — which aren't in DEPLOYABLE_PIECES — aren't
    // silently dropped from a preset. Each piece is clamped to its copy cap;
    // cards absent from the preset reset to 0.
    for (const pack of PACKS) {
      for (const id of pack.pieces) {
        props.onChange(id, Math.min(build[id] ?? 0, pieceDef(id).maxCopies ?? Infinity));
      }
      for (const id of pack.abilities) props.onChange(id, build[id] ?? 0);
    }
  }

  return (
    <div class="panel">
      <div class="field-row" style={{ "margin-bottom": "12px", "justify-content": "space-between" }}>
        <div class="field" style={{ flex: "1" }}>
          <label>{`Player ${props.owner + 1} name`}</label>
          <input
            type="text"
            value={props.name}
            onInput={(e) => props.onName(e.currentTarget.value)}
            style={{ "border-left": `3px solid ${PLAYER_ACCENT[props.owner]}` }}
          />
        </div>
      </div>

      <div class="builder-budget">
        <span class="budget-big" classList={{ over: over() }}>
          {remaining()}
        </span>
        <span class="budget-sub">
          points left · {spent()}/{props.config.budget} spent · {buildSize(props.build)} cards
        </span>
      </div>
      <div class="bar">
        <div class="bar-fill" classList={{ over: over() }} style={{ width: `${pct()}%` }} />
      </div>

      <For each={PACKS}>
        {(pack) => (
          <div class="pack">
            <div class="pack-head">
              <span class="builder-section">{pack.name}</span>
              <span class="pack-tagline">{pack.tagline}</span>
            </div>
            <div class="piece-list">
              <For each={packEntries(pack)}>
                {(entry) => {
                  const count = () => props.build[entry.id] ?? 0;
                  const atMax = () => entry.max !== undefined && count() >= entry.max;
                  const canAdd = () => !atMax() && spent() + entry.cost <= props.config.budget;
                  return (
                    <div class="piece-row">
                      <Show
                        when={entry.pieceType}
                        fallback={<span class="glyph glyph-ability">{entry.glyph}</span>}
                      >
                        {(pt) => (
                          <span class="glyph">
                            <PieceIcon type={pt()} owner={props.owner} />
                          </span>
                        )}
                      </Show>
                      <div class="meta">
                        <div class="name">{entry.name}</div>
                        <div class="cost">
                          {entry.cost} {entry.cost === 1 ? "point" : "points"}
                          {entry.max !== undefined ? ` · max ${entry.max}` : ""}
                        </div>
                        <div class="pdesc">{entry.desc}</div>
                      </div>
                      <Show when={entry.pieceType} fallback={<span class="mp-slot" />}>
                        {(pt) => <MovePattern type={pt()} owner={props.owner} />}
                      </Show>
                      <div class="stepper">
                        <button
                          class="round"
                          onClick={() => props.onChange(entry.id, Math.max(0, count() - 1))}
                          disabled={count() === 0}
                          aria-label={`Remove ${entry.name}`}
                        >
                          −
                        </button>
                        <span class="count">{count()}</span>
                        <button
                          class="round"
                          onClick={() => props.onChange(entry.id, count() + 1)}
                          disabled={!canAdd()}
                          aria-label={`Add ${entry.name}`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                }}
              </For>
            </div>
          </div>
        )}
      </For>

      <div class="presets">
        <span class="budget-sub" style={{ "align-self": "center" }}>
          Starter decks:
        </span>
        <For each={STARTER_DECKS}>
          {(deck) => (
            <button class="btn" title={deck.blurb} onClick={() => applyPreset(deck.build)}>
              {deck.name}
            </button>
          )}
        </For>
        <button class="btn btn-ghost" onClick={() => applyPreset({})}>
          Clear
        </button>
      </div>

      <p class="budget-sub" style={{ "margin-top": "12px" }}>
        Your King and Prince are already on the board. {pieceDef("queen").name}s and others wait in
        your deck until you play them.
      </p>
    </div>
  );
}
